import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowDown, ArrowRightLeft, ArrowUp, Archive, ChevronDown, Copy, Download, ExternalLink, FileDown, FileText, Play,
  RefreshCw, Search, Trash2, Undo2, Upload as UploadIcon, X,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ShareLinkManager from '../components/ShareLinkManager';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import DatePicker from '../ui/DatePicker';
import ExpandedRowPanel from '../ui/ExpandedRowPanel';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Menu from '../ui/Menu';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import StageChip from '../ui/StageChip';
import Tag from '../ui/Tag';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { useUploads, useUploadDone } from '../shell/uploads';

const VIDEO_FILE = /\.(mp4|mov|webm|avi|mpeg|mpg)$/i;

// AI production on src/ui (DESIGN rev 2.1 §3.1: Grid at high density, grouped by stage, the Expanded-row panel in place
// of the old video-card modal). Same calls as before: GET /api/ai-production, /api/clients, /api/team-members;
// PATCH /:id/status (row and panel), PATCH /:id (panel fields), DELETE /:id (bulk delete); the panel's video calls
// (GET /api/video-assets?aiProductionId, /:id/download, DELETE /:id, POST + GET /api/downloads/zip) and GET /api/scripts/:id
// for the CSV export. The route is mounted only when aiProductionEnabled (App.jsx).

const PIPELINE_STEPS = ['Scripted', 'Editing', 'Qual', 'Approved', 'Posted'];
const AI_TRANSITIONS = {
  Scripted: ['Editing'],
  Editing:  ['Qual',     'Scripted'],
  Qual:     ['Approved', 'Editing'],
  Approved: ['Posted',   'Qual'],
  Posted:   ['Approved'],
};
const SORTS = [
  { value: 'Script / Title', label: 'title' },
  { value: 'Project', label: 'client' },
  { value: 'Editor', label: 'editor' },
  { value: 'Status', label: 'stage' },
  { value: 'Posted', label: 'posted date' },
];
const FORWARD_LABEL = { Editing: 'Send to editing', Qual: 'Send for qual', Approved: 'Approve', Posted: 'Mark posted' };
const COL_LABEL = {
  narration: 'Narration', visuals: 'Visuals',
  character: 'Character', line: 'Line', notes: 'Notes',
  scene: 'Scene Description', props: 'Props',
  aRoll: 'A Roll', bRoll: 'B Roll', vfx: 'VFX', sfx: 'SFX',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function shortDate(str) {
  if (!str) return '';
  // Date-only strings parse as a local date so the UTC offset cannot shift the day.
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function toInputDate(str) {
  if (!str) return '';
  return typeof str === 'string' ? str.slice(0, 10) : new Date(str).toISOString().slice(0, 10);
}
const titleOf = r => clean(r.script?.scriptName || r.notes || 'Untitled video');
// A cut in Qual is waiting on a reviewer (§2.1 "Needs review"); the other stages carry none.
const conditionOf = r => (r.status === 'Qual' ? 'Needs review' : null);

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AiProduction() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [records,      setRecords]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(false);
  const [search,       setSearch]       = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [clientFilter, setClientFilter] = useState(() => searchParams.get('client') || '');
  const [editorFilter, setEditorFilter] = useState(() => searchParams.get('editor') || '');
  const [aiSort,       setAiSort]       = useState(() => ({ col: searchParams.get('sortBy') || '', dir: searchParams.get('sortDir') || 'asc' }));
  const [clients,      setClients]      = useState([]);
  const [members,      setMembers]      = useState([]);
  const [selectedId,   setSelectedId]   = useState(null);
  const [menuFor,      setMenuFor]      = useState(null);
  const [busyId,       setBusyId]       = useState(null);
  const [selectedCards, setSelectedCards] = useState(() => new Set());

  const roles = user?.roles || [];
  const canMassDelete = roles.includes('Admin') || roles.includes('Power User') || roles.includes('Post Lead') || roles.includes('Post_Lead') || roles.includes('Producer');

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      if (clientFilter) params.set('clientId', clientFilter);
      if (editorFilter) params.set('editorId', editorFilter);
      if (statusFilter) params.set('status', statusFilter);
      else params.set('excludePosted', 'true');
      const res = await api.get(`/api/ai-production?${params}`);
      setRecords(res.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [clientFilter, statusFilter, editorFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // Deep link from a notification (navigation state) or ?card=: open that record's panel once the rows are in.
  useEffect(() => {
    const openId = location.state?.openRecordId || searchParams.get('card');
    if (!openId || records.length === 0) return;
    if (selectedId === openId) return;
    if (records.some(r => r.id === openId)) setSelectedId(openId);
    if (location.state?.openRecordId) navigate('/ai' + location.search, { replace: true, state: {} });
  }, [location, records, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the open panel into ?card= so a refresh restores it (skipping the first run, before the deep link opens it).
  const cardUrlSyncInit = useRef(false);
  useEffect(() => {
    if (!cardUrlSyncInit.current) { cardUrlSyncInit.current = true; return; }
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (selectedId) p.set('card', selectedId); else p.delete('card');
      return p;
    }, { replace: true });
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters and sort live in the URL so the back button restores them.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (search) p.set('q', search); else p.delete('q');
      if (statusFilter) p.set('status', statusFilter); else p.delete('status');
      if (clientFilter) p.set('client', clientFilter); else p.delete('client');
      if (editorFilter) p.set('editor', editorFilter); else p.delete('editor');
      if (aiSort.col) p.set('sortBy', aiSort.col); else p.delete('sortBy');
      if (aiSort.dir !== 'asc') p.set('sortDir', aiSort.dir); else p.delete('sortDir');
      return p;
    }, { replace: true });
  }, [aiSort, search, statusFilter, clientFilter, editorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ─────────────────────────────────────────────────────────────────

  const statusCounts = useMemo(() => {
    const c = {};
    records.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [records]);

  const editorOptions = useMemo(() => members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User')), [members]);

  const filtered = useMemo(() => {
    if (!search) return records;
    const q = search.toLowerCase();
    return records.filter(r =>
      r.script?.scriptName?.toLowerCase().includes(q) ||
      r.notes?.toLowerCase().includes(q) ||
      r.client?.name?.toLowerCase().includes(q) ||
      r.editor?.name?.toLowerCase().includes(q));
  }, [records, search]);

  const rows = useMemo(() => {
    if (!aiSort.col) return filtered;
    const { col, dir } = aiSort;
    return [...filtered].sort((a, b) => {
      let av = '', bv = '';
      if      (col === 'Script / Title') { av = a.script?.scriptName || ''; bv = b.script?.scriptName || ''; }
      else if (col === 'Project')        { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (col === 'Editor')         { av = a.editor?.name || ''; bv = b.editor?.name || ''; }
      else if (col === 'Status')         { av = a.status || ''; bv = b.status || ''; }
      else if (col === 'Posted')         { av = a.postedDate || ''; bv = b.postedDate || ''; }
      else return 0;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filtered, aiSort]);

  const groups = useMemo(() => {
    const present = new Set(rows.map(r => r.status));
    return PIPELINE_STEPS.filter(s => present.has(s)).map(s => ({ key: s, header: <StageChip stage={s} /> }));
  }, [rows]);

  const filtersOn = !!(search || statusFilter || clientFilter || editorFilter);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setClientFilter(''); setEditorFilter(''); };

  // ── Actions ─────────────────────────────────────────────────────────────────

  function handleRecordUpdated(updated) {
    setRecords(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
  }

  async function changeStatus(record, newStatus) {
    setMenuFor(null);
    setBusyId(record.id);
    try {
      const res = await api.patch(`/api/ai-production/${record.id}/status`, { status: newStatus, changedBy: user.id });
      handleRecordUpdated({ ...record, ...res.data });
      toast(`${titleOf(record)} moved to ${newStatus}.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  }

  async function handleMassDelete() {
    const ids = [...selectedCards];
    const ok = await confirm({
      title: `Delete ${plural(ids.length, 'AI video')}?`,
      body: `This permanently deletes ${ids.length === 1 ? 'this AI video' : `these ${ids.length} AI videos`}. Linked scripts are not affected.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await Promise.all(ids.map(id => api.delete(`/api/ai-production/${id}`)));
      setRecords(prev => prev.filter(r => !selectedCards.has(r.id)));
      setSelectedCards(new Set());
      toast(`${plural(ids.length, 'AI video')} deleted.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'One or more AI videos were not deleted. The list has been reloaded.', { tone: 'danger' });
      fetchRecords();
    }
  }

  const toggleCard = id => setSelectedCards(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = rows.length > 0 && rows.every(r => selectedCards.has(r.id));

  // ── Header ──────────────────────────────────────────────────────────────────

  usePageHeader({
    title: 'AI production',
    meta: loading ? 'Loading' : plural(records.length, 'video'),
    actions: (
      <>
        {canMassDelete && selectedCards.size > 0 && (
          <Button variant="danger" icon={Trash2} onClick={handleMassDelete}>Delete {plural(selectedCards.size, 'video')}</Button>
        )}
        <Button variant="quiet" icon={RefreshCw} onClick={fetchRecords}>Refresh</Button>
      </>
    ),
    filters: (
      <>
        <Input icon={Search} aria-label="Search by script, notes, client or editor" placeholder="Search by script, notes, client or editor"
          value={search} onChange={e => setSearch(e.target.value)} />
        <Select aria-label="Stage" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          options={[{ value: '', label: 'Active (hides Posted)' }, ...PIPELINE_STEPS.map(s => ({ value: s, label: statusCounts[s] != null ? `${s} (${statusCounts[s]})` : s }))]} />
        <Select aria-label="Client" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          options={[{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        <Select aria-label="Editor" value={editorFilter} onChange={e => setEditorFilter(e.target.value)}
          options={[{ value: '', label: 'All editors' }, ...editorOptions.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        <Select aria-label="Sort" value={aiSort.col} onChange={e => setAiSort(s => ({ ...s, col: e.target.value }))}
          options={[{ value: '', label: 'Newest first' }, ...SORTS.map(s => ({ value: s.value, label: `Sort by ${s.label}` }))]} />
        {aiSort.col && (
          <Button variant="quiet" iconOnly icon={aiSort.dir === 'asc' ? ArrowUp : ArrowDown}
            label={aiSort.dir === 'asc' ? 'Ascending, switch to descending' : 'Descending, switch to ascending'}
            onClick={() => setAiSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))} />
        )}
        {filtersOn && <Button variant="quiet" icon={X} onClick={clearFilters}>Clear</Button>}
      </>
    ),
  });

  // ── Grid ────────────────────────────────────────────────────────────────────

  const columns = [
    ...(canMassDelete ? [{
      key: 'select', width: 56,
      label: <Checkbox label={<span className="ui-sr">Select every row in the view</span>} checked={allSelected}
        onChange={e => setSelectedCards(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} />,
      render: r => (
        <span onClick={e => e.stopPropagation()}>
          <Checkbox label={<span className="ui-sr">Select {titleOf(r)}</span>} checked={selectedCards.has(r.id)} onChange={() => toggleCard(r.id)} />
        </span>
      ),
    }] : []),
    { key: 'title', label: 'Title', width: 300, render: r => <>{titleOf(r)}{r.script?.inTranscript && <> <Tag>In transcript</Tag></>}</> },
    { key: 'stage', label: 'Stage', width: 124, render: r => <StageChip stage={r.status} /> },
    { key: 'condition', label: 'Condition', width: 132, render: r => (conditionOf(r) ? <Badge condition={conditionOf(r)} /> : null) },
    { key: 'client', label: 'Client', width: 150, render: r => (r.client?.name ? <Tag>{clean(r.client.name)}</Tag> : <span className="ui-meta">No client</span>) },
    { key: 'editor', label: 'Editor', width: 140, render: r => (r.editor?.name ? clean(r.editor.name) : <span className="ui-meta">Unassigned</span>) },
    { key: 'posted', label: 'Posted', width: 88, render: r => shortDate(r.postedDate) || null },
    { key: 'notes', label: 'Notes', width: 220, render: r => (r.notes ? clean(r.notes) : null) },
    { key: 'updated', label: 'Updated', width: 88, render: r => shortDate(r.updatedAt) },
  ];

  const rowActions = r => {
    const title = titleOf(r);
    const transitions = AI_TRANSITIONS[r.status] || [];
    return (
      <>
        {transitions.length > 0 && (
          <Popover open={menuFor === r.id} onOpenChange={o => setMenuFor(o ? r.id : null)} align="end" label={`Change the stage of ${title}`}
            trigger={({ toggle, ref, props }) => (
              <Button ref={ref} variant="quiet" iconOnly icon={ArrowRightLeft} label={`Change the stage of ${title}`} loading={busyId === r.id} onClick={toggle} {...props} />
            )}>
            <Menu label={`Move ${title} to`} onClose={() => setMenuFor(null)}
              items={transitions.map(t => ({ label: `Move to ${t}`, onSelect: () => changeStatus(r, t) }))} />
          </Popover>
        )}
        {r.frameLink && (
          <a className="ui ui-btn ui-btn--quiet ui-btn--icon" href={r.frameLink} target="_blank" rel="noreferrer"
            aria-label={`Open the Frame.io review of ${title}`} title="Frame.io review">
            <Icon icon={Play} />
          </a>
        )}
        <a className="ui ui-btn ui-btn--quiet ui-btn--icon" href={`/ai?card=${r.id}`} target="_blank" rel="noreferrer"
          aria-label={`Open ${title} in a new tab`} title="Open in a new tab">
          <Icon icon={ExternalLink} />
        </a>
      </>
    );
  };

  const selected = selectedId ? records.find(r => r.id === selectedId) : null;

  return (
    <div className="ui ui-page ui-page--flush">
      <div className="ui-work">
        <Grid
          label="AI production"
          columns={columns} rows={rows} rowKey={r => r.id} groups={groups} groupOf={r => r.status}
          density="high" storageKey="hf-grid:ai" selectedKey={selectedId}
          onOpen={r => setSelectedId(r.id)} rowActions={rowActions}
          state={loadError ? 'error' : loading ? 'loading' : 'data'}
          empty={filtersOn
            ? { message: 'No AI video matches these filters. Clear them to see every active AI video.', action: { label: 'Clear filters', icon: X, onClick: clearFilters } }
            : { message: 'No AI video is in production. An AI script gets its video here once it is approved.' }}
          error={{ title: 'AI production did not load', message: 'Check your connection, then try again.', onRetry: fetchRecords }} />
        {selected && (
          <AiPanel key={selected.id} record={selected} members={members} onClose={() => setSelectedId(null)} onUpdated={handleRecordUpdated} />
        )}
      </div>
    </div>
  );
}

// ── Expanded-row panel (was the AI video-card modal) ─────────────────────────

function AiPanel({ record, members, onClose, onUpdated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [form, setForm] = useState({
    editorId:   record.editorId  ?? '',
    frameLink:  record.frameLink ?? '',
    postedDate: toInputDate(record.postedDate),
    notes:      record.notes     ?? '',
  });
  const [dirty,         setDirty]         = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [videoAsset,    setVideoAsset]    = useState(null);
  // HD-5: uploads go through the global Upload sheet (shell/uploads.js), the same queue Qual uses.
  const uploads = useUploads();
  const fileInput = useRef(null);
  const upload = uploads.items.find(i => i.meta.productionId === record.id && i.status !== 'done');
  useUploadDone(item => { if (item.meta.productionId === record.id) setVideoAsset(item.result); });
  const [assetLoading,  setAssetLoading]  = useState(true);
  const [legacyOpen,    setLegacyOpen]    = useState(false);
  const [zipJob,        setZipJob]        = useState(null);
  const zipPollRef = useRef(null);

  const setField = (key, value) => { setForm(prev => ({ ...prev, [key]: value })); setDirty(true); };

  const roles = user?.roles || [];
  const editors = members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User'));
  const pipelineIdx = PIPELINE_STEPS.indexOf(record.status);
  const isAdmin = roles.includes('Admin') || roles.includes('Power User');
  const isShootLead = roles.includes('Producer');
  const canApprove = isAdmin || roles.includes('Qual Reviewer');
  const canEditVideoCard = isAdmin || roles.includes('Editor') || isShootLead;
  const visibleTransitions = (AI_TRANSITIONS[record.status] || []).filter(t => t !== 'Approved' || canApprove);

  useEffect(() => {
    api.get(`/api/video-assets?aiProductionId=${record.id}`)
      .then(r => setVideoAsset(r.data))
      .catch(() => {})
      .finally(() => setAssetLoading(false));
  }, [record.id]);

  useEffect(() => () => { if (zipPollRef.current) clearInterval(zipPollRef.current); }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await api.patch(`/api/ai-production/${record.id}`, {
        editorId:   form.editorId   || null,
        frameLink:  form.frameLink  || null,
        postedDate: form.postedDate || null,
        notes:      form.notes      || null,
      });
      onUpdated({ ...record, ...res.data });
      setDirty(false);
      toast('Changes saved.');
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The changes did not save. Try again.', { tone: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange(newStatus) {
    setTransitioning(true);
    try {
      const res = await api.patch(`/api/ai-production/${record.id}/status`, { status: newStatus, changedBy: user.id });
      onUpdated({ ...record, ...res.data });
      toast(`Moved to ${newStatus}.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' });
    } finally {
      setTransitioning(false);
    }
  }

  async function handleDownload() {
    try {
      const res = await api.get(`/api/video-assets/${videoAsset.id}/download`);
      // A presigned URL with Content-Disposition: attachment, so the browser downloads instead of playing inline.
      const a = document.createElement('a');
      a.href = res.data.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast('The download did not start. Try again.', { tone: 'danger' });
    }
  }

  async function handleZipDownload() {
    try {
      setZipJob({ status: 'processing' });
      const res = await api.post('/api/downloads/zip', { videoAssetIds: [videoAsset.id] });
      const { jobId } = res.data;
      zipPollRef.current = setInterval(async () => {
        try {
          const poll = await api.get(`/api/downloads/zip/${jobId}`);
          if (poll.data.status === 'complete') {
            clearInterval(zipPollRef.current);
            setZipJob({ status: 'complete', downloadUrl: poll.data.downloadUrl });
          } else if (poll.data.status === 'failed') {
            clearInterval(zipPollRef.current);
            setZipJob(null);
            toast('The zip could not be made. Try again.', { tone: 'danger' });
          }
        } catch {
          clearInterval(zipPollRef.current);
          setZipJob(null);
        }
      }, 3000);
    } catch {
      setZipJob(null);
      toast('The zip download did not start. Try again.', { tone: 'danger' });
    }
  }

  // R-2: removing a video always archives it; it is never deleted from storage.
  async function handleArchiveVideo() {
    const ok = await confirm({
      title: 'Archive this video?',
      body: `${clean(videoAsset.fileName || 'The video')} is archived, never deleted from storage. The file and its comments are kept; archived versions are listed in the review page's version menu, where they can be restored. Only an Admin can permanently delete an archive.`,
      confirmLabel: 'Archive video',
    });
    if (!ok) return;
    try {
      await api.delete(`/api/video-assets/${videoAsset.id}`);
      setVideoAsset(null);
      toast('Video archived.');
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The video was not archived. Try again.', { tone: 'danger' });
    }
  }

  async function handleExportCSV() {
    if (!record.script?.id) { toast('No script is linked to this video.', { tone: 'warn' }); return; }
    try {
      const res = await api.get(`/api/scripts/${record.script.id}`);
      const script = res.data;
      const scenes = script.scriptBody?.scenes || [];
      if (scenes.length === 0) { toast('The script has no scenes to export.', { tone: 'warn' }); return; }
      const keys = Object.keys(scenes[0]).filter(k => !k.startsWith('_'));
      const esc = v => `"${String(v || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
      const header = keys.map(k => esc(COL_LABEL[k] || k)).join(',');
      const lines = scenes.map(row => keys.map(k => esc(row[k] || '')).join(','));
      const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${script.scriptName}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('The script did not export. Try again.', { tone: 'danger' });
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(`${window.location.origin}/ai?card=${record.id}`)
      .then(() => toast('Link copied.'))
      .catch(() => toast('The link was not copied. Try again.', { tone: 'danger' }));
  }

  function openReview() {
    const p = new URLSearchParams(location.search);
    p.set('card', record.id);
    navigate(`/review/${videoAsset.id}`, { state: { returnTo: `${location.pathname}?${p.toString()}` } });
  }

  const frameLinkField = canEditVideoCard ? (
    <div className="ui-cluster">
      <Input label="Frame.io review link" value={form.frameLink} onChange={e => setField('frameLink', e.target.value)} placeholder="https://app.frame.io/" />
      {form.frameLink?.startsWith('http') && (
        <a className="ui ui-btn ui-btn--secondary" href={form.frameLink} target="_blank" rel="noreferrer"><Icon icon={ExternalLink} />Open</a>
      )}
    </div>
  ) : form.frameLink?.startsWith('http') ? (
    <span><a className="ui ui-btn ui-btn--secondary" href={form.frameLink} target="_blank" rel="noreferrer"><Icon icon={ExternalLink} />Review video</a></span>
  ) : <span className="ui-meta">No Frame.io link</span>;

  return (
    <ExpandedRowPanel open title={titleOf(record)} onClose={onClose} label="AI video details"
      footer={(
        <>
          {visibleTransitions.map(t => {
            const back = PIPELINE_STEPS.indexOf(t) < pipelineIdx;
            return (
              <Button key={t} variant={back ? 'secondary' : 'primary'} icon={back ? Undo2 : undefined} disabled={transitioning} onClick={() => handleStatusChange(t)}>
                {back ? `Back to ${t}` : FORWARD_LABEL[t] || t}
              </Button>
            );
          })}
          {dirty && <Button variant="secondary" loading={saving} onClick={handleSave}>Save changes</Button>}
        </>
      )}>
      <div className="ui-stack">
        <dl className="ui-kv">
          <dt>Stage</dt><dd><StageChip stage={record.status} /></dd>
          {conditionOf(record) && <><dt>Condition</dt><dd><Badge condition={conditionOf(record)} /></dd></>}
          <dt>Client</dt><dd>{record.client?.name ? clean(record.client.name) : 'No client'}</dd>
          <dt>Script</dt><dd>{record.script?.scriptName ? clean(record.script.scriptName) : 'No script linked'}</dd>
          <dt>Updated</dt><dd>{shortDate(record.updatedAt)}</dd>
        </dl>
        <div className="ui-cluster">
          {record.script?.id && (
            <Button variant="secondary" icon={FileText}
              onClick={() => { onClose(); navigate('/scripts', { state: { openScriptId: record.script.id, fromVideoCard: true, isShootLeadForScript: isShootLead } }); }}>
              View script
            </Button>
          )}
          {record.script?.id && <Button variant="quiet" icon={FileDown} onClick={handleExportCSV}>Export CSV</Button>}
          <Button variant="quiet" icon={Copy} onClick={copyLink}>Copy link</Button>
        </div>

        <section className="ui-section" aria-labelledby="ai-video">
          <h3 className="ui-heading" id="ai-video">Video</h3>
          {assetLoading ? (
            <Skeleton variant="block" height={180} label="Loading the video" />
          ) : videoAsset ? (
            <>
              <div className="ui ui-monitor">
                <div className="ui-monitor__frame ui-monitor__frame--16x9">
                  {videoAsset.thumbnailUrl
                    ? <img className="ui-cover" src={videoAsset.thumbnailUrl} alt="" />
                    : <Skeleton variant="block" label="Thumbnail generating" />}
                </div>
              </div>
              <span className="ui-meta">
                {clean(videoAsset.fileName)}{(videoAsset.openCommentCount ?? 0) > 0 ? `, ${plural(videoAsset.openCommentCount, 'open comment')}` : ''}
              </span>
              <div className="ui-cluster">
                <Button variant="secondary" icon={Play} onClick={openReview}>Open review</Button>
                <Button variant="quiet" icon={Download} onClick={handleDownload}>Download video</Button>
                {zipJob?.status !== 'complete' && (
                  <Button variant="quiet" icon={Download} loading={zipJob?.status === 'processing'} onClick={handleZipDownload}>
                    {zipJob?.status === 'processing' ? 'Preparing zip' : 'Download as zip'}
                  </Button>
                )}
                {zipJob?.status === 'complete' && zipJob.downloadUrl && (
                  <a className="ui ui-btn ui-btn--secondary" href={zipJob.downloadUrl} target="_blank" rel="noreferrer"><Icon icon={Download} />Download zip</a>
                )}
                {(canEditVideoCard || videoAsset.uploadedBy === user?.id) && (
                  <Button variant="danger" icon={Archive} onClick={handleArchiveVideo}>Archive video</Button>
                )}
              </div>
            </>
          ) : canEditVideoCard ? (
            upload ? (
              <p className="ui-p ui-dense ui-text-2" role="status">
                {upload.status === 'failed'
                  ? `${clean(upload.name)} did not upload. Retry it from the upload sheet.`
                  : `Uploading ${clean(upload.name)}, ${Math.round(upload.progress * 100)}%. You can keep working; the upload sheet shows its progress.`}
              </p>
            ) : (
              <div className="ui-cluster">
                <Button variant="secondary" icon={UploadIcon} onClick={() => fileInput.current?.click()}>Choose video</Button>
                <span className="ui-dense ui-text-3">MP4, MOV, WebM or AVI.</span>
                <input ref={fileInput} type="file" accept=".mp4,.mov,.webm,.avi,.mpeg,.mpg" hidden onChange={e => {
                  const file = e.target.files?.[0]; e.target.value = '';
                  if (!file) return;
                  if (!VIDEO_FILE.test(file.name) && !(file.type || '').startsWith('video/')) { toast('Choose a video file: MP4, MOV, WebM or AVI.', { tone: 'warn' }); return; }
                  uploads.enqueueVideo({ file, clientId: record.clientId, aiProductionId: record.id });
                }} />
              </div>
            )
          ) : (
            <p className="ui-p ui-dense ui-text-3">No video uploaded yet.</p>
          )}
        </section>

        <Select label="Editor" value={form.editorId} onChange={e => setField('editorId', e.target.value)} disabled={!canEditVideoCard}
          placeholder="Not assigned" options={editors.map(m => ({ value: m.id, label: clean(m.name) }))} />

        {videoAsset ? (
          <div className="ui-stack ui-stack--tight">
            <span>
              <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={legacyOpen} onClick={() => setLegacyOpen(v => !v)}>
                <Icon icon={ChevronDown} />Legacy Frame.io link
              </button>
            </span>
            {legacyOpen && frameLinkField}
          </div>
        ) : frameLinkField}

        <DatePicker label="Posted date" value={form.postedDate} onChange={e => setField('postedDate', e.target.value)} disabled={!canEditVideoCard} />
        <Textarea label="Notes" rows={4} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Internal notes" readOnly={!canEditVideoCard} />

        {videoAsset && <ShareLinkManager videoAssetId={videoAsset.id} />}
      </div>
    </ExpandedRowPanel>
  );
}
